import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  TablePagination,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import categoryService from '../../services/categoryService';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import Layout from '../../components/layout/Layout';
import salesmanService from '../../services/salesmanService';
import salesmanItemWiseSalesService from '../../services/salesmanItemWiseSalesService';
import authService from '../../services/authService';
import { formatCurrency } from '../../utils/formatCurrency';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';

const todayIso = () => new Date().toISOString().slice(0, 10);

const parseOptionalInt = (value) => {
  if (value === '' || value == null) return null;
  const n = parseInt(String(value), 10);
  return Number.isNaN(n) ? null : n;
};

const filterFieldSx = {
  width: '100%',
  '& .MuiInputBase-root': { height: 56 },
};

const filterGridSx = (isAdmin) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: isAdmin ? 'repeat(7, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
  },
  gap: 2,
  alignItems: 'stretch',
});

const SalesmanItemWiseSalesReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    rows: [],
    totalRevenue: 0,
    totalQuantitySold: 0,
    totalProducts: 0,
    salesmanName: null,
  });
  const [startDate, setStartDate] = useState(todayIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [salesmanId, setSalesmanId] = useState('');
  const [search, setSearch] = useState('');
  const [salesmen, setSalesmen] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(15);
  const [isExporting, setIsExporting] = useState(false);

  const showSalesmanColumn = !appliedFilters?.salesmanId;

  const appliedCategoryName = useMemo(() => {
    if (!appliedFilters?.categoryId) return null;
    const cat = categories.find((c) => c.id === appliedFilters.categoryId);
    return cat?.name || null;
  }, [appliedFilters, categories]);

  const initializeUserContext = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      const userData = response.data || {};
      const role = userData.role || authService.getUserRole();
      setIsAdmin(role === 'ADMIN');
      setUserLocation(userData.location || null);
    } catch {
      setIsAdmin(authService.getUserRole() === 'ADMIN');
      setUserLocation(null);
    } finally {
      setFiltersReady(true);
    }
  }, []);

  const fetchSalesmen = useCallback(async () => {
    try {
      if (isAdmin) {
        const list = await salesmanService.list();
        setSalesmen(Array.isArray(list) ? list : []);
      } else if (userLocation?.id) {
        const list = await salesmanService.list({ locationId: userLocation.id });
        setSalesmen(Array.isArray(list) ? list : []);
      } else {
        setSalesmen([]);
      }
    } catch {
      setSalesmen([]);
    }
  }, [isAdmin, userLocation]);

  const fetchLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.LOCATIONS}?size=1000`);
      setLocations(response.data.content || []);
    } catch {
      setLocations([]);
    }
  }, [isAdmin]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await categoryService.getAllCategories({ page: 0, size: 500, sort: 'name', direction: 'ASC' });
      const list = Array.isArray(res) ? res : (res?.content || []);
      setCategories(list);
    } catch {
      setCategories([]);
    }
  }, []);

  const fetchReport = useCallback(async (overrides = {}) => {
    setLoading(true);
    const effectiveStart = overrides.startDate ?? startDate;
    const effectiveEnd = overrides.endDate ?? endDate;
    const effectiveSalesmanId = overrides.salesmanId !== undefined ? overrides.salesmanId : salesmanId;
    const effectiveCategoryId = overrides.categoryId !== undefined ? overrides.categoryId : categoryId;
    const effectiveSearch = overrides.search !== undefined ? overrides.search : search;
    const effectiveLocation = overrides.selectedLocation !== undefined
      ? overrides.selectedLocation
      : (isAdmin ? selectedLocation : userLocation?.id);

    const locationParam = parseOptionalInt(effectiveLocation);
    const salesmanParam = parseOptionalInt(effectiveSalesmanId);
    const categoryParam = parseOptionalInt(effectiveCategoryId);

    try {
      const data = await salesmanItemWiseSalesService.getSalesmanItemWiseSales({
        startDate: effectiveStart,
        endDate: effectiveEnd,
        salesmanId: salesmanParam,
        locationId: locationParam,
        categoryId: categoryParam,
        search: effectiveSearch,
      });

      setReportData({
        rows: data.rows || [],
        totalRevenue: Number(data.totalRevenue || 0),
        totalQuantitySold: Number(data.totalQuantitySold || 0),
        totalProducts: Number(data.totalProducts || 0),
        salesmanName: data.salesmanName || null,
      });
      setAppliedFilters({
        startDate: effectiveStart,
        endDate: effectiveEnd,
        salesmanId: salesmanParam,
        locationId: locationParam,
        categoryId: categoryParam,
        search: (effectiveSearch || '').trim(),
      });
      setPage(0);
    } catch (e) {
      console.error('Salesman item-wise report error', e);
      toast.error('Failed to load salesman item-wise sales report');
      setReportData({
        rows: [],
        totalRevenue: 0,
        totalQuantitySold: 0,
        totalProducts: 0,
        salesmanName: null,
      });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, salesmanId, categoryId, search, isAdmin, selectedLocation, userLocation]);

  const handleApply = () => {
    fetchReport({
      startDate,
      endDate,
      salesmanId,
      categoryId,
      search,
      selectedLocation: isAdmin ? selectedLocation : userLocation?.id,
    });
  };

  useEffect(() => {
    initializeUserContext();
  }, [initializeUserContext]);

  useEffect(() => {
    if (filtersReady) {
      fetchSalesmen();
      fetchLocations();
      fetchCategories();
    }
  }, [filtersReady, fetchSalesmen, fetchLocations, fetchCategories]);

  useEffect(() => {
    if (filtersReady) {
      fetchReport();
    }
    // Initial load only; user clicks Apply to refresh after changing filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  const paginatedRows = useMemo(
    () => reportData.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [reportData.rows, page, rowsPerPage]
  );

  const handleExport = () => {
    try {
      setIsExporting(true);
      const headers = showSalesmanColumn
        ? ['S.No', 'Salesman', 'Product', 'SKU', 'Category', 'Qty Sold', 'Total Value']
        : ['S.No', 'Product', 'SKU', 'Category', 'Qty Sold', 'Total Value'];

      const dataRows = reportData.rows.map((r, i) => {
        const base = [
          i + 1,
          ...(showSalesmanColumn ? [r.salesmanName || 'Unassigned'] : []),
          r.productName || '',
          r.sku || '',
          r.categoryName || '',
          Number(r.quantitySold || 0),
          Number(r.totalRevenue || 0),
        ];
        return base;
      });

      const ws = XLSX.utils.aoa_to_sheet([
        ['Item-wise Sales Report'],
        [`Period: ${startDate} to ${endDate}`],
        appliedFilters?.salesmanId && reportData.salesmanName ? [`Salesman: ${reportData.salesmanName}`] : [],
        appliedCategoryName ? [`Category: ${appliedCategoryName}`] : [],
        [],
        ['Total products', reportData.totalProducts],
        ['Total qty sold', Number(reportData.totalQuantitySold)],
        ['Total value', Number(reportData.totalRevenue)],
        [],
        headers,
        ...dataRows,
      ]);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Salesman Item Sales');
      XLSX.writeFile(wb, `salesman_item_wise_sales_${startDate}_to_${endDate}.xlsx`);
      toast.success('Report exported');
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const formatQty = (q) => {
    const n = Number(q);
    if (Number.isNaN(n)) return '0';
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={reportPageSx.container}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={reportPageSx.breadcrumbs}>
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="text.primary">Item-wise Sales</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, ...reportPageSx.pageHeader }}>
          <Box>
            <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
              <InventoryIcon color="primary" />
              Item-wise Sales
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Product quantity and value sold — filter by date, salesman, category, or location.
              {appliedCategoryName && (
                <> Active category: <strong>{appliedCategoryName}</strong>.</>
              )}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || reportData.rows.length === 0}
          >
            Export Excel
          </Button>
        </Box>

        <Card sx={reportPageSx.filterCard}>
          <CardContent sx={reportPageSx.filterCardContent}>
            <Box sx={filterGridSx(isAdmin)}>
              <TextField
                label="From date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={filterFieldSx}
              />
              <TextField
                label="To date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={filterFieldSx}
              />
              <FormControl sx={filterFieldSx}>
                <InputLabel>Salesman</InputLabel>
                <Select
                  value={salesmanId}
                  label="Salesman"
                  onChange={(e) => setSalesmanId(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {salesmen.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isAdmin && (
                <FormControl sx={filterFieldSx}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={selectedLocation}
                    label="Location"
                    onChange={(e) => setSelectedLocation(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={String(loc.id)}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl sx={filterFieldSx}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryId}
                  label="Category"
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Product / SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                sx={filterFieldSx}
              />
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={loading}
                sx={{ ...filterFieldSx, minHeight: 56, ...reportPageSx.filterButton }}
              >
                Apply
              </Button>
            </Box>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={reportPageSx.metricsGrid}>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Products (lines)"
                  value={reportData.totalProducts}
                  icon={<InventoryIcon />}
                  accentColor="primary.main"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Total qty sold"
                  value={formatQty(reportData.totalQuantitySold)}
                  icon={<TrendingUpIcon />}
                  accentColor="info.main"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ReportMetricCard
                  label="Total value"
                  value={formatCurrency(reportData.totalRevenue)}
                  icon={<CurrencyRupeeIcon />}
                  accentColor="success.main"
                  valueColor="success.main"
                />
              </Grid>
            </Grid>

            <Card sx={reportPageSx.sectionCard}>
              <CardContent sx={reportPageSx.sectionCardContent}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      {showSalesmanColumn && <TableCell>Salesman</TableCell>}
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Qty sold</TableCell>
                      <TableCell align="right">Total value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={showSalesmanColumn ? 7 : 6} align="center">
                          No sales found for the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRows.map((row, idx) => (
                        <TableRow key={`${row.salesmanId}-${row.productId}-${idx}`} hover>
                          <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                          {showSalesmanColumn && (
                            <TableCell>{row.salesmanName || 'Unassigned'}</TableCell>
                          )}
                          <TableCell>{row.productName || '–'}</TableCell>
                          <TableCell>{row.sku || '–'}</TableCell>
                          <TableCell>{row.categoryName || '–'}</TableCell>
                          <TableCell align="right">{formatQty(row.quantitySold)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.totalRevenue)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={reportData.rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[15]}
              />
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Layout>
  );
};

export default SalesmanItemWiseSalesReport;
